import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, LayoutDashboard, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AccountSettingsModal from './AccountSettingsModal';
import ManageSubscriptionModal from './ManageSubscriptionModal';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { artNavCtaBtn, artNavPillBtn } from '../ui/artmosphereButtonTheme';

export default function ArtmosphereNavbar({ title = "Artmosphere", subtitle = "Creative Community", hideAccountButton = false, embedded = false }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [accountSettingsInitialTab, setAccountSettingsInitialTab] = useState('consent');
  const [showManageSubscription, setShowManageSubscription] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const handleHomeClick = () => {
    navigate('/my-art-world');
  };
  const handleHomePageClick = () => {
    // Home: always go to HomePageNew (no redirect)
    navigate('/home');
  };

  // Close dropdowns when global close event dispatched or on Escape
  useEffect(() => {
    const closeAll = () => {
      setShowAccountDropdown(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') closeAll();
    };
    const onClickOutside = (e) => {
      const accountRoot = document.querySelector('.account-dropdown');
      const clickedInsideAccount = accountRoot && accountRoot.contains(e.target);
      if (!clickedInsideAccount) {
        closeAll();
      }
    };
    window.addEventListener('ui-close-popovers', closeAll);
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      window.removeEventListener('ui-close-popovers', closeAll);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const wrapperClass = embedded
    ? 'py-2 sm:py-3 artm-page'
    : 'bg-[#FFFCF9]/90 backdrop-blur-sm border-b border-[#F5D99B]/55 shadow-sm artm-page';
  const innerClass = embedded ? 'px-0' : 'max-w-6xl mx-auto px-3 sm:px-6 py-2 sm:py-3';

  const navBtnPrimary = `${artNavCtaBtn} min-h-[2.25rem] min-w-[2.25rem]`;
  const navBtnGhost = `${artNavPillBtn} min-h-[2.25rem] min-w-[2.25rem] text-[#5c4428]`;

  return (
      <div className={wrapperClass}>
      <div className={innerClass}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="art-nav-logo h-8 w-8 text-base sm:h-10 sm:w-10 sm:rounded-xl sm:text-xl">
              A
            </div>
            <div className="min-w-0">
              <h1 className="auth-premium-title truncate text-base sm:text-xl md:text-2xl">{title}</h1>
              <p className="auth-premium-subtitle hidden text-[10px] line-clamp-1 sm:block sm:text-sm">{subtitle}</p>
            </div>
          </div>
          
          {/* Right side — icon-first on mobile */}
          <div className="artm-toolbar artm-toolbar-scroll flex-shrink-0 w-full sm:w-auto">
            <button
              type="button"
              onClick={handleHomeClick}
              className={navBtnPrimary}
              title="Artist Corner"
              aria-label="Artist Corner"
            >
              <LayoutDashboard className="w-4 h-4 shrink-0" strokeWidth={2} />
              <span className="hidden sm:inline">Artist Corner</span>
            </button>
            <button
              type="button"
              onClick={handleHomePageClick}
              className={navBtnGhost}
              title="Home"
              aria-label="Home"
            >
              <Home className="w-4 h-4 shrink-0" strokeWidth={2} />
              <span className="hidden sm:inline">Home</span>
            </button>

            {user && !hideAccountButton && (
              <div className="relative account-dropdown">
                <button
                  type="button"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('ui-close-popovers', { detail: 'account-dropdown' }));
                    setShowAccountDropdown((prev) => !prev);
                  }}
                  className={navBtnGhost}
                  title="My Account"
                  aria-label="My Account"
                  aria-expanded={showAccountDropdown}
                >
                  <User className="w-4 h-4 shrink-0" strokeWidth={2} />
                  <span className="hidden sm:inline">Account</span>
                </button>
                {showAccountDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-[9999]">
                    <div className="py-2">
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => { setAccountSettingsInitialTab('consent'); setShowAccountSettings(true); setShowAccountDropdown(false); }}
                        className="block w-full text-left px-4 py-2 text-gray-900 hover:bg-gray-50 transition cursor-pointer"
                      >
                        Account Settings
                      </button>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => { setAccountSettingsInitialTab('orders'); setShowAccountSettings(true); setShowAccountDropdown(false); }}
                        className="block w-full text-left px-4 py-2 text-gray-900 hover:bg-gray-50 transition cursor-pointer"
                      >
                        My Orders
                      </button>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => { setShowManageSubscription(true); setShowAccountDropdown(false); }}
                        className="block w-full text-left px-4 py-2 text-gray-900 hover:bg-gray-50 transition cursor-pointer"
                      >
                        Manage Subscription
                      </button>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => { handleSignOut(); setShowAccountDropdown(false); }}
                        className="block w-full text-left px-4 py-2 text-gray-900 hover:bg-gray-50 transition cursor-pointer"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Account Settings Modal */}
      <AccountSettingsModal isOpen={showAccountSettings} onClose={() => setShowAccountSettings(false)} initialTab={accountSettingsInitialTab} />

      <ManageSubscriptionModal isOpen={showManageSubscription} onClose={() => setShowManageSubscription(false)} />
    </div>
  );
}
